/**
 * Layout component
 *
 * Main app layout with sidebar, content area, and global toast notifications.
 */

import { useState, useEffect } from 'react';
import { Sidebar } from '../Sidebar';
import { ToastContainer } from '../Toast';
import { useAuth } from '../../hooks';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  // Close sidebar on route change or resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = () => {
    setSidebarOpen(false);
  };

  return (
    <div className={`app ${sidebarOpen ? 'sidebar-open' : ''}`} data-testid="app-layout">
      {/* Mobile hamburger button - only show when authenticated */}
      {isAuthenticated && (
        <button
          className="hamburger-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? 'Zatvori meni' : 'Otvori meni'}
          aria-expanded={sidebarOpen}
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      )}

      {/* Sidebar with mobile overlay */}
      <Sidebar onClose={() => setSidebarOpen(false)} />
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      <main className="main">{children}</main>

      {/* Global toast notifications */}
      <ToastContainer />
    </div>
  );
}

export default Layout;
